import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import FactoryFlag from './FactoryFlag';
import './FactoryTargetModal.css';

/* 공장별 일일 목표 생산량 설정 — 전사 통합 대시보드의 "생산 목표 대비 달성" 톱니 버튼.
 * 공장마다 하루 목표(샷 수)를 입력하고, 전사 목표는 그 합으로 자동 계산된다.
 * 목표는 바꾸기 전까지 매일 적용된다(서버 factory_production_target). 비우면 그 공장 목표를 지운다.
 * 저장은 onSave([{ factoryId, targetProduction }]) 로 넘기고, 실패 메시지는 모달 안에 보인다. 스타일은 ftm- 접두어.
 *
 * factories: [{ id, name, country, production, target }]  canEdit: factory.update 권한 여부 */

const isPositiveInt = (v) => /^\d+$/.test(String(v)) && Number(v) > 0;

function FactoryTargetModal({ isOpen, onClose, onSave, factories = [], canEdit = true, t }) {
  const tr = (k, p) => t(`pages.monitoring.enterprise.v2.targetModal.${k}`, p);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    const init = {};
    factories.forEach((f) => { init[f.id] = f.target ? String(f.target) : ''; });
    setValues(init);
    setError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, saving, onClose]);

  const invalidIds = useMemo(
    () => Object.entries(values).filter(([, v]) => v !== '' && !isPositiveInt(v)).map(([id]) => id),
    [values]
  );
  const total = useMemo(
    () => Object.values(values).filter(isPositiveInt).reduce((s, v) => s + Number(v), 0),
    [values]
  );

  if (!isOpen) return null;

  const save = async () => {
    if (invalidIds.length > 0) { setError(tr('invalid')); return; }
    setSaving(true);
    setError('');
    try {
      await onSave(factories.map((f) => ({ factoryId: f.id, targetProduction: isPositiveInt(values[f.id]) ? Number(values[f.id]) : null })));
      onClose();
    } catch (e) {
      console.error('Factory target save failed:', e);
      setError(/404/.test(String(e?.message)) ? tr('notDeployed') : /403/.test(String(e?.message)) ? tr('forbidden') : tr('saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="ftm-overlay" onMouseDown={() => !saving && onClose()}>
      <div className="ftm-modal" role="dialog" aria-label={tr('title')} onMouseDown={(e) => e.stopPropagation()}>
        <div className="ftm-head">
          <h3 className="ftm-title">{tr('title')}</h3>
          <button type="button" className="ftm-icon-btn" onClick={onClose} disabled={saving} aria-label={t('common.close')}>
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
        <p className="ftm-hint">{tr('hint')}</p>
        {!canEdit && <p className="ftm-warn">{tr('noPermission')}</p>}

        <div className="ftm-list">
          <div className="ftm-row ftm-row-head">
            <span>{tr('factory')}</span>
            <span>{tr('today')}</span>
            <span>{tr('target')}</span>
          </div>
          {factories.map((f) => {
            const bad = invalidIds.includes(String(f.id));
            return (
              <label key={f.id} className="ftm-row">
                <span className="ftm-name"><FactoryFlag country={f.country} cls="ftm-flag" size={18} />{f.name}</span>
                <span className="ftm-today">{f.production == null ? '—' : Number(f.production).toLocaleString()}</span>
                <span className="ftm-input-wrap">
                  <input
                    className={`ftm-input ${bad ? 'invalid' : ''}`}
                    inputMode="numeric"
                    placeholder={tr('placeholder')}
                    value={values[f.id] ?? ''}
                    disabled={!canEdit || saving}
                    onChange={(e) => setValues((v) => ({ ...v, [f.id]: e.target.value.replace(/[^\d]/g, '') }))}
                    onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
                  />
                  <small>{tr('unit')}</small>
                </span>
              </label>
            );
          })}
        </div>

        <div className="ftm-total">
          <span>{tr('total')}</span>
          <b>{total > 0 ? total.toLocaleString() : '—'}<small>{tr('unit')}</small></b>
        </div>
        {error && <p className="ftm-error">{error}</p>}

        <div className="ftm-foot">
          <button type="button" className="ftm-btn ftm-btn-outline" onClick={onClose} disabled={saving}>{t('common.cancel')}</button>
          <button type="button" className="ftm-btn ftm-btn-primary" onClick={save} disabled={!canEdit || saving}>{saving ? tr('saving') : t('common.save')}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default FactoryTargetModal;
